// ============================================================
// Student Profile Page
// ============================================================

import { useState, useEffect } from 'react';
import { Box, Tabs, Tab, Typography, Button, Chip, Avatar, Stack } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/navigation/PageHeader';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useStudent } from '@/hooks/useStudent';
import { useSchool } from '@/hooks/useSchool';
import useStudentStore from '@/store/studentStore';
import { Loader } from '@/components/feedback/Loader';
import { ErrorState } from '@/components/feedback/ErrorState';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { tokens } from '@/theme/tokens';
import { StudentProfileHeader } from './components/StudentProfileHeader';
import { StudentIDCard } from './components/StudentIDCard';

function TabPanel({ children, value, index }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
      {value === index && children}
    </Box>
  );
}

export function StudentProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { schoolId } = useSchool();
  const { student: fullData, loading, error } = useStudent(id);
  const updateStudentStatus = useStudentStore((s) => s.updateStudentStatus);
  const fetchStudentById = useStudentStore((s) => s.fetchStudentById);
  const [tabValue, setTabValue] = useState(0);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    setTabValue(0);
  }, [id]);

  usePageMeta('Student Profile', [
    { label: 'Students', path: '/students' },
    { label: 'List', path: '/students/list' },
    { label: 'Profile' },
  ]);

  const handleStatusChange = async (newStatus) => {
    if (!schoolId || !id) return;
    setUpdatingStatus(true);
    try {
      const res = await updateStudentStatus(schoolId, id, newStatus);
      toast.success(res?.data?.message || res?.message || `Student status updated to ${newStatus}`);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update student status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) return <Loader message="Loading student profile..." />;
  if (error) return <ErrorState message={error} onRetry={() => window.location.reload()} />;
  if (!fullData) return <ErrorState message="Student not found" />;

  const student = fullData.student || {};
  const parent = fullData.parent || {};
  const address = fullData.address || {};
  const enrollment = fullData.enrollment || {};
  const siblings = Array.isArray(fullData.siblings) ? fullData.siblings : [];

  return (
    <Box sx={{ pb: 6 }}>
      <PageHeader 
        title="Student Profile" 
        subtitle="View detailed information about the student."
        action={
          <Button 
            variant="outlined" 
            startIcon={<ArrowLeft size={18} />}
            onClick={() => navigate('/students/list')}
          >
            Back to List
          </Button>
        }
      />
      
      <StudentProfileHeader 
        student={fullData} 
        onStatusChange={handleStatusChange}
        updatingStatus={updatingStatus}
      />

      <Box sx={{ mt: 4, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, v) => setTabValue(v)} 
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
            }
          }}
        >
          <Tab label="Overview" />
          <Tab label="Academic" />
          <Tab label="Parents" />
          <Tab label="Address" />
          <Tab label="Documents" />
          <Tab label={`Siblings${siblings.length > 0 ? ` (${siblings.length})` : ''}`} />
          <Tab label="ID Card" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          <Box sx={{ flex: 2 }}>
            <WidgetContainer title="Basic Information">
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Admission No</Typography>
                  <Typography variant="body2" fontWeight={500}>{student.admission_no || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">SR Number</Typography>
                  <Typography variant="body2" fontWeight={500}>{student.sr_number || student.sr_no || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Full Name</Typography>
                  <Typography variant="body2" fontWeight={500}>{`${student.first_name || ''} ${student.middle_name || ''} ${student.last_name || ''}`.trim() || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
                  <Typography variant="body2" fontWeight={500}>{student.date_of_birth || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Gender</Typography>
                  <Typography variant="body2" fontWeight={500}>{student.gender || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Blood Group</Typography>
                  <Typography variant="body2" fontWeight={500}>{student.blood_group || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Aadhaar No</Typography>
                  <Typography variant="body2" fontWeight={500}>{student.aadhaar_no || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Religion</Typography>
                  <Typography variant="body2" fontWeight={500}>{student.religion || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Category</Typography>
                  <Typography variant="body2" fontWeight={500}>{student.category || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Caste</Typography>
                  <Typography variant="body2" fontWeight={500}>{student.caste || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Nationality</Typography>
                  <Typography variant="body2" fontWeight={500}>{student.nationality || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Is RTE Student</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {student.is_rte !== undefined ? (student.is_rte ? 'Yes' : 'No') : (student.isRte ? 'Yes' : 'No')}
                  </Typography>
                </Box>
              </Box>
            </WidgetContainer>
          </Box>
          <Box sx={{ flex: 1 }}>
             <WidgetContainer title="Contact Information">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Email Address</Typography>
                  <Typography variant="body2" fontWeight={500}>{student.email || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Mobile Number</Typography>
                  <Typography variant="body2" fontWeight={500}>{student.mobile || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Father's Phone</Typography>
                  <Typography variant="body2" fontWeight={500}>{parent.father_mobile || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Mother's Phone</Typography>
                  <Typography variant="body2" fontWeight={500}>{parent.mother_mobile || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Guardian Phone</Typography>
                  <Typography variant="body2" fontWeight={500}>{parent.guardian_mobile || '-'}</Typography>
                </Box>
              </Box>
            </WidgetContainer>
          </Box>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <WidgetContainer title="Enrollment Details">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Session</Typography>
                <Typography variant="body2" fontWeight={500}>{enrollment.academic_session?.session_name || enrollment.academic_session_id?.session_name || enrollment.academic_session_id || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Class & Section</Typography>
                <Typography variant="body2" fontWeight={500}>{enrollment.class?.class_name || enrollment.class_id?.class_name || enrollment.class_id || '-'} - {enrollment.section?.section_name || enrollment.section_id?.section_name || enrollment.section_id || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Roll Number</Typography>
                <Typography variant="body2" fontWeight={500}>{enrollment.roll_number || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Admission Date</Typography>
                <Typography variant="body2" fontWeight={500}>{enrollment.admission_date || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Admission Type</Typography>
                <Typography variant="body2" fontWeight={500}>{enrollment.admission_type || 'Regular'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Typography variant="body2" fontWeight={500}>{enrollment.status || '-'}</Typography>
              </Box>
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography variant="caption" color="text.secondary">Remarks</Typography>
                <Typography variant="body2" fontWeight={500}>{enrollment.remarks || '-'}</Typography>
              </Box>
            </Box>
          </WidgetContainer>
        </Box>

          <Box sx={{ flex: 1 }}>
            <WidgetContainer title="Previous Academics">
              {(!fullData.previous_academics || fullData.previous_academics.length === 0) ? (
                <Typography variant="body2" color="text.secondary">No previous academic records found.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {fullData.previous_academics.map((record, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        p: 2.5, 
                        bgcolor: `${tokens.colors.primary.main}05`, 
                        border: `1px solid ${tokens.colors.border}`,
                        borderRadius: tokens.radius.md 
                      }}
                    >
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">School Name</Typography>
                          <Typography variant="body2" fontWeight={600}>{record.school_name || '-'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Board & Medium</Typography>
                          <Typography variant="body2" fontWeight={500}>{record.board || '-'} {record.medium ? `(${record.medium})` : ''}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Last Class Passed</Typography>
                          <Typography variant="body2" fontWeight={500}>{record.last_class || '-'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Percentage / Grade</Typography>
                          <Typography variant="body2" fontWeight={500}>{record.percentage ? `${record.percentage}%` : '-'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">TC Number</Typography>
                          <Typography variant="body2" fontWeight={500}>{record.tc_number || '-'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">TC Issue Date</Typography>
                          <Typography variant="body2" fontWeight={500}>{record.tc_date || '-'}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </WidgetContainer>
          </Box>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <WidgetContainer title="Father's Details">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Name</Typography>
                <Typography variant="body2" fontWeight={500}>{parent.father_name || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Mobile</Typography>
                <Typography variant="body2" fontWeight={500}>{parent.father_mobile || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                <Typography variant="body2" fontWeight={500}>{parent.father_email || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Occupation</Typography>
                <Typography variant="body2" fontWeight={500}>{parent.father_occupation || '-'}</Typography>
              </Box>
            </Box>
            </WidgetContainer>
          </Box>

          <Box sx={{ flex: 1 }}>
            <WidgetContainer title="Mother's Details">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Name</Typography>
                <Typography variant="body2" fontWeight={500}>{parent.mother_name || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Mobile</Typography>
                <Typography variant="body2" fontWeight={500}>{parent.mother_mobile || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                <Typography variant="body2" fontWeight={500}>{parent.mother_email || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Occupation</Typography>
                <Typography variant="body2" fontWeight={500}>{parent.mother_occupation || '-'}</Typography>
              </Box>
            </Box>
            </WidgetContainer>
          </Box>

          <Box sx={{ flex: 1 }}>
            <WidgetContainer title="Guardian & Other Details">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Guardian Name</Typography>
                <Typography variant="body2" fontWeight={500}>{parent.guardian_name || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Guardian Relation</Typography>
                <Typography variant="body2" fontWeight={500}>{parent.guardian_relation || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Guardian Mobile</Typography>
                <Typography variant="body2" fontWeight={500}>{parent.guardian_mobile || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Annual Income</Typography>
                <Typography variant="body2" fontWeight={500}>{parent.annual_income != null ? `₹${parent.annual_income}` : '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Primary Login</Typography>
                <Typography variant="body2" fontWeight={500}>{parent.primary_login || 'FATHER'}</Typography>
              </Box>
            </Box>
            </WidgetContainer>
          </Box>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <WidgetContainer title="Current Address">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography variant="caption" color="text.secondary">Address Line</Typography>
                <Typography variant="body2" fontWeight={500}>{address.current_address || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">City</Typography>
                <Typography variant="body2" fontWeight={500}>{address.current_city || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">State</Typography>
                <Typography variant="body2" fontWeight={500}>{address.current_state || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Country</Typography>
                <Typography variant="body2" fontWeight={500}>{address.current_country || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Pincode</Typography>
                <Typography variant="body2" fontWeight={500}>{address.current_pincode || '-'}</Typography>
              </Box>
            </Box>
            </WidgetContainer>
          </Box>

          <Box sx={{ flex: 1 }}>
            <WidgetContainer title="Permanent Address">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography variant="caption" color="text.secondary">Address Line</Typography>
                <Typography variant="body2" fontWeight={500}>{address.permanent_address || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">City</Typography>
                <Typography variant="body2" fontWeight={500}>{address.permanent_city || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">State</Typography>
                <Typography variant="body2" fontWeight={500}>{address.permanent_state || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Country</Typography>
                <Typography variant="body2" fontWeight={500}>{address.permanent_country || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Pincode</Typography>
                <Typography variant="body2" fontWeight={500}>{address.permanent_pincode || '-'}</Typography>
              </Box>
            </Box>
            </WidgetContainer>
          </Box>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <WidgetContainer title="Documents">
          {(!fullData.documents || fullData.documents.length === 0) ? (
            <Typography variant="body2" color="text.secondary">No documents uploaded.</Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
               {fullData.documents.map((doc, index) => {
                 const docTitle = doc.document_type
                   ? doc.document_type
                       .replace(/_/g, ' ')
                       .replace(/\//g, ' / ')
                       .toLowerCase()
                       .replace(/\b\w/g, (c) => c.toUpperCase())
                   : `Document ${index + 1}`;

                 return (
                   <Box 
                     key={index} 
                     sx={{ 
                       p: 2.5, 
                       bgcolor: '#fff',
                       border: `1px solid ${tokens.colors.border}`, 
                       borderRadius: tokens.radius.md,
                       display: 'flex',
                       flexDirection: 'column',
                       justifyContent: 'space-between',
                       gap: 2,
                       boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                     }}
                   >
                     <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                         <Box 
                           sx={{ 
                             width: 40, 
                             height: 40, 
                             borderRadius: 2, 
                             bgcolor: `${tokens.colors.primary.main}12`,
                             color: tokens.colors.primary.main,
                             display: 'flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                             fontWeight: 700
                           }}
                         >
                           📄
                         </Box>
                         <Box>
                           <Typography variant="body1" fontWeight={600} color="text.primary">
                             {docTitle}
                           </Typography>
                           {doc.document_number && (
                             <Typography variant="caption" color="text.secondary">
                               Doc No: {doc.document_number}
                             </Typography>
                           )}
                         </Box>
                       </Box>
                       <Chip 
                         label={doc.verified ? 'Verified' : 'Pending'} 
                         size="small"
                         color={doc.verified ? 'success' : 'warning'}
                         sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                       />
                     </Box>

                     {doc.file_url && (
                       <Button 
                         component="a"
                         href={doc.file_url}
                         target="_blank"
                         rel="noopener noreferrer"
                         variant="outlined" 
                         size="small"
                         sx={{ alignSelf: 'flex-start', textTransform: 'none', borderRadius: 1.5 }}
                       >
                         View Document ↗
                       </Button>
                     )}
                   </Box>
                 );
               })}
            </Box>
          )}
        </WidgetContainer>
      </TabPanel>

      {/* Siblings Tab */}
      <TabPanel value={tabValue} index={5}>
        <WidgetContainer title={`Sibling Information (${siblings.length})`}>
          {siblings.length === 0 ? (
            <Box
              sx={{
                py: 4,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Users size={36} color={tokens.colors.primary.main} opacity={0.4} />
              <Typography variant="body2" color="text.secondary">
                No siblings linked to this student.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
                gap: 2.5,
              }}
            >
              {siblings.map((sib, idx) => {
                const relationLabel = (sib.relationship || '')
                  .replace(/_/g, ' ')
                  .toLowerCase()
                  .replace(/\b\w/g, (c) => c.toUpperCase());

                const relationColor =
                  sib.relationship?.includes('SISTER') ? 'secondary'
                  : sib.relationship?.includes('TWIN') ? 'info'
                  : sib.relationship?.includes('STEP') ? 'warning'
                  : 'primary';

                const siblingTargetId = sib.sibling_student_id || sib.student_id || sib.sibling_id || sib.id;

                const className = sib.class?.name || sib.class_name || (typeof sib.class === 'string' ? sib.class : '');
                const sectionName = sib.section?.name || sib.section_name || (typeof sib.section === 'string' ? sib.section : '');

                return (
                  <Box
                    key={sib.id || siblingTargetId || idx}
                    onClick={() => {
                      if (siblingTargetId) {
                        navigate(`/students/${siblingTargetId}`);
                      }
                    }}
                    sx={{
                      p: 2.5,
                      border: `1px solid ${tokens.colors.border}`,
                      borderRadius: tokens.radius.md,
                      bgcolor: 'background.paper',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      cursor: siblingTargetId ? 'pointer' : 'default',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': siblingTargetId ? {
                        boxShadow: '0 6px 18px rgba(0,0,0,0.1)',
                        borderColor: tokens.colors.primary.main,
                        transform: 'translateY(-2px)',
                      } : {},
                    }}
                  >
                    <Avatar
                      src={sib.photo}
                      alt={sib.student_name}
                      sx={{ width: 56, height: 56, border: `2px solid ${tokens.colors.primary.main}30` }}
                    >
                      {(sib.student_name || 'S').charAt(0).toUpperCase()}
                    </Avatar>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography
                          variant="body1"
                          fontWeight={700}
                          noWrap
                          sx={{
                            color: siblingTargetId ? 'text.primary' : 'text.primary',
                            transition: 'color 0.2s',
                            '.MuiBox-root:hover &': {
                              color: tokens.colors.primary.main,
                            },
                          }}
                        >
                          {sib.student_name || '-'}
                        </Typography>
                        <Chip
                          label={relationLabel}
                          size="small"
                          color={relationColor}
                          variant="outlined"
                          sx={{ fontWeight: 600, fontSize: 11, height: 20 }}
                        />
                      </Box>

                      <Stack direction="row" spacing={2} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
                        {sib.admission_no && (
                          <Typography variant="caption" color="text.secondary">
                            📋 {sib.admission_no}
                          </Typography>
                        )}
                        {(className || sectionName) && (
                          <Typography variant="caption" color="text.secondary">
                            🏫 {className}{sectionName ? ` — ${sectionName}` : ''}
                          </Typography>
                        )}
                        {sib.roll_number && (
                          <Typography variant="caption" color="text.secondary">
                            🎫 Roll {sib.roll_number}
                          </Typography>
                        )}
                        {sib.gender && (
                          <Typography variant="caption" color="text.secondary">
                            {sib.gender === 'MALE' ? '♂' : '♀'} {sib.gender}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </WidgetContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={6}>
        <StudentIDCard fullData={fullData} />
      </TabPanel>
    </Box>
  );
}
